/** W82 Design-Web-1 (D-1661, Teil B a): Ablegen-Flug nach „Passt“, Kopie von `9c93e244:web/src/neu/ablegen.ts` (Entwurf) mit
 *  Anpassungen (eigener Name, weil `seiten/ablegen.ts` im Bestand Fokus und Meldung trägt). Nach „Passt“ hebt sich der Brief
 *  dort, wo der Blick auf der Karte liegt, faltet sich zum Umschlag und fliegt im Bogen dorthin, wo abgelegte Post liegt: mobil
 *  in den Reiter „Akten“, am Desktop in „Archiv“ der Seitenleiste oder der Rail. Das Ziel stößt an und leuchtet in der Farbe des
 *  Funkens der Palette (Bestand: Nachtblau Petrol, Tintenblau gelb). Vorbild iOS `Vorschlagskarte.swift:115-121`, `BuehneFlug.swift`.
 *  Nur transform und opacity (Web Animations API), Ebene ohne Zeiger; die Karte, ihre Haltezeit und der Fokus bleiben Bestand.
 *  Reduzierte Bewegung: nur das Leuchten am Ziel (Blende 200 ms); `?still`: nichts; ruhige Fassung: Federn ohne Überschwingen. */
import { bewegungReduziert, bogen3, feder, glatt, klemme, mix, ruhigeFassung, spur, stillGeschaltet } from "./feder";
import { briefBauen, dunkelVon, glanzVon, koerperTf, schattenTf, type BriefDaten } from "./papierbrief";

function sichtbar(el: Element | null): el is HTMLElement | SVGElement {
  if (!(el instanceof HTMLElement || el instanceof SVGElement)) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
}

/** Wo abgelegte Post liegt: Reiter „Akten“ (mobil), sonst „Archiv“ in Seitenleiste oder Rail. */
export function ablageZiel(): HTMLElement | SVGElement | null {
  const kandidaten = [
    document.querySelector('nav.tab-pille a[href="/akten"] .tab-pille-ikon'),
    document.querySelector('nav.leiste-voll a[href="/blaettern"] svg'),
    document.querySelector('nav.leiste-rail a[href="/blaettern"] svg'),
  ];
  return kandidaten.find(sichtbar) ?? null;
}

/** Wo der Brief sichtbar ist: der Beleg, sonst der Kopf, sonst die Knöpfe, sonst die Karte (mindestens zur Hälfte im Bild). */
export function ausgang(karte: Element | null): Element | null {
  if (!karte) return null;
  const imBild = (el: Element | null) => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.height > 0 && (Math.min(r.bottom, innerHeight) - Math.max(r.top, 0)) / r.height >= 0.5;
  };
  for (const sel of [".v-beleg", ".v-fakten", ".v-knoepfe-reihe"]) {
    const el = karte.querySelector(sel);
    if (imBild(el)) return el;
  }
  return karte;
}

function ebeneOben(): HTMLDivElement {
  const ebene = document.createElement("div");
  ebene.className = "bue-flugebene bue-flugebene-oben";
  ebene.setAttribute("aria-hidden", "true");
  document.body.append(ebene);
  return ebene;
}

/** Ziel anstoßen und leuchten lassen (reduziert nur das Leuchten als Blende). */
export function zielAnstossen(ziel: HTMLElement | SVGElement, verzug = 0): void {
  if (stillGeschaltet()) return;
  const r = ziel.getBoundingClientRect();
  const ebene = ebeneOben();
  const licht = document.createElement("div");
  licht.className = "bue-ziel-licht";
  licht.style.left = `${(r.left + r.width / 2).toFixed(1)}px`;
  licht.style.top = `${(r.top + r.height / 2).toFixed(1)}px`;
  ebene.append(licht);
  const reduziert = bewegungReduziert();
  const a = licht.animate(
    reduziert
      ? [{ opacity: 0 }, { opacity: 0.85, offset: 0.4 }, { opacity: 0 }]
      : [
          { transform: "translate(-50%, -50%) scale(0.4)", opacity: 0 },
          { transform: "translate(-50%, -50%) scale(0.95)", opacity: 0.9, offset: 0.25 },
          { transform: "translate(-50%, -50%) scale(1.5)", opacity: 0 },
        ],
    { duration: reduziert ? 200 : 760, delay: verzug, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "both" },
  );
  a.onfinish = () => ebene.remove();
  a.oncancel = () => ebene.remove();
  if (reduziert) return;
  // weicher Stoß in 90 ms auf 1,18, dann Feder zurück (ruhig: ohne Überschwingen)
  const f = ruhigeFassung() ? feder(1, 640 * 1.15) : feder(0.5, 640);
  const n = 40;
  const bilder: Keyframe[] = [];
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * (f.dauer + 90);
    const s = t < 90 ? 1 + 0.18 * Math.sin((Math.PI / 2) * (t / 90)) : 1 + 0.18 * (1 - f(t - 90));
    bilder.push({ transform: `scale(${s.toFixed(4)})`, offset: i / n });
  }
  ziel.animate(bilder, { duration: f.dauer + 90, delay: verzug, easing: "linear" });
}

/** Der Brief fliegt von `von` zum Ziel. Rückgabe die Zeit bis zur Ankunft in ms (0 ohne Flug). */
export function briefAblegen(von: Element | null, daten: BriefDaten, ziel: HTMLElement | SVGElement | null = ablageZiel()): number {
  if (!von || !ziel || stillGeschaltet()) return 0;
  if (bewegungReduziert()) {
    zielAnstossen(ziel);
    return 0;
  }
  const q = von.getBoundingClientRect();
  const z = ziel.getBoundingClientRect();
  const ebene = ebeneOben();
  const L = klemme(q.width * 0.42, 110, 170);
  const b = briefBauen(daten, L, ebene, "mitte");
  const A: [number, number] = [q.left + q.width / 2, q.top + Math.min(q.height / 2, 70)];
  const Z: [number, number] = [z.left + z.width / 2, z.top + z.height / 2];
  // Bogen: erst nach oben (vom Tisch abheben), dann im Bogen zum Ziel
  const hebe = Math.max(60, Math.abs(Z[1] - A[1]) * 0.35);
  const weg = bogen3(A, [A[0], A[1] - hebe], [Z[0] + (A[0] - Z[0]) * 0.15, Z[1] - hebe * 0.8], Z, 0.3);
  const ruhig = ruhigeFassung();
  const falt = 240;
  const VORLAUF = 70;
  const f = ruhig ? feder(1, 700 * 1.15) : feder(0.85, 700);
  const aufF = feder(1, 380);
  const tAnkunft = falt + VORLAUF + f.erreicht;
  const ende = falt + VORLAUF + f.dauer * 0.62;
  const laufend = spur(
    [0, ende],
    60,
    (t) => {
      let x: number, y: number, rx: number, rz: number, s: number, o: number, hoehe: number;
      if (t < falt) {
        // zusammenfalten: der Bogen wird zum Umschlag (kippt nach vorn, wird kleiner, erscheint)
        const p = glatt(t / falt);
        x = A[0];
        y = A[1];
        rx = mix(-38, 0, p);
        rz = mix(-2, 0, p);
        s = mix(1.22, 1, p);
        o = klemme(t / 90, 0, 1);
        hoehe = 0.4;
      } else {
        const lt = t - falt;
        const dip = lt > 0 && lt < VORLAUF + 20 ? Math.sin((Math.PI * lt) / (VORLAUF + 20)) : 0;
        const p = f(lt - VORLAUF);
        const pos = weg(p);
        const vx = (weg(f(lt - VORLAUF + 10))[0] - weg(f(lt - VORLAUF - 10))[0]) / 20;
        const pp = klemme(p, 0, 1);
        const auf = aufF(lt - 20);
        x = pos[0];
        y = pos[1] + dip * 3;
        rx = mix(0, -6, auf) * (1 - pp);
        rz = klemme(vx * 7, -14, 14) * (1 - pp * 0.6);
        s = (1 - dip * 0.03) * (pp < 0.2 ? mix(1, 1.07, pp / 0.2) : mix(1.07, 0.16, glatt((pp - 0.2) / 0.8)));
        o = 1 - glatt((t - (tAnkunft - 120)) / 120);
        hoehe = klemme(auf, 0, 1) * (1 - pp * 0.7);
      }
      const sx = (1 + hoehe * 0.28) * s;
      const sy = sx * Math.max(0.55, Math.cos((rx * Math.PI) / 180));
      return {
        k: { transform: koerperTf(x, y, b.w, b.h, rx, 0, rz, s), opacity: o },
        s: { transform: schattenTf(x + 2 + hoehe * 10, y + 4 + hoehe * 26, b.w * 0.78, b.h * 0.7, rz, sx, sy), opacity: o * mix(0.45, 0.12, hoehe) },
        g: { opacity: klemme(glanzVon(rx, 0), 0, 0.75) * o },
        d: { opacity: dunkelVon(rx, 0) * o },
      };
    },
    { k: b.koerper, s: b.schatten, g: b.glanz, d: b.dunkel },
  );
  const erste = laufend[0];
  if (erste) {
    erste.onfinish = () => ebene.remove();
    erste.oncancel = () => ebene.remove();
  } else ebene.remove();
  zielAnstossen(ziel, tAnkunft - 40);
  return tAnkunft;
}
